import { LETTER_TYPE } from "@/libraries/constant";
import type { Case, CaseTeam, ContactMember } from "@/types";
import { fallback } from "@tanstack/router-zod-adapter";
import dayjs, { type Dayjs } from "dayjs";
import { z } from "zod";
import { DataIdSchema } from "./auth";
import { MESSAGE_TAG } from "./message";
import { CreateCaseSearchParamSchema } from "./page";

export const CaseSearchParamSchema = z.object({
	q: z.string().optional(),
});

export type CaseSearchParamSchema = z.infer<typeof CaseSearchParamSchema>;

export const CreateCaseFormFieldSchema = z.object({
	step: z.number().int(),
	year_of_birth: z.any().transform((val) => {
		if (dayjs.isDayjs(val)) {
			return +val.format("YYYY");
		}
		return val;
	}),
	sex: z.preprocess((val) => {
		if (typeof val === "string") {
			const lower = val.toLowerCase();
			if (lower === "male") return "Male";
			if (lower === "female") return "Female";
			if (lower === "other") return "Other";
		}
		return val;
	}, z.enum(["Male", "Female", "Other"])),
	title: z
		.string()
		.trim()
		.min(1, "Field is required")
		.max(50, "Title must be less than 50 characters")
		.optional(),
	summary: z
		.string()
		.trim()
		.max(200, "Summary must be less than 200 characters")
		.optional(),
	patient: z
		.object({
			id: DataIdSchema.optional(),
			name: z.string().trim().optional(),
			surname: z.string().trim().optional(),
			dob: z
				.any()
				.transform((val) => {
					if (dayjs.isDayjs(val)) {
						return val.format("DD/MM/YYYY");
					}
					return val;
				})
				.optional(),
			address: z.string().trim().optional(),
			ic_number: z.string().trim().optional(),
		})
		.optional(),
});

export type CreateCaseFormFieldSchema = z.infer<
	typeof CreateCaseFormFieldSchema
>;

export const CASE_TABS_VALUE = {
	ALL: "all",
	AI: MESSAGE_TAG.AI,
	DIAGNOSIS: MESSAGE_TAG.DIAGNOSIS,
	PLAN: MESSAGE_TAG.PLAN,
	FILES: MESSAGE_TAG.FILES,
	EXTERNAL: MESSAGE_TAG.EXTERNAL,
	CARECHAT: "carechat",
} as const;

export type CASE_TABS_VALUE =
	(typeof CASE_TABS_VALUE)[keyof typeof CASE_TABS_VALUE];

export const CaseChatSearchSchema = z.object({
	tab: fallback(z.nativeEnum(CASE_TABS_VALUE), CASE_TABS_VALUE.ALL).default(
		CASE_TABS_VALUE.ALL,
	),
	subtab: z.string().optional(),
	q: z.string().optional(),
	mentions: z.array(DataIdSchema).optional(),
	mid: DataIdSchema.optional(),
});

export type CaseChatSearchSchema = z.infer<typeof CaseChatSearchSchema>;

export const GetCaseSyncParamsSchema = z.object({
	last_requested_at: z.number().int(),
	case_ids: z.array(DataIdSchema).optional(),
	status: z.enum(["active", "closed"]).optional(),
	page: z.number().int().optional(),
	limit: z.number().int().optional(),
});

export type GetCaseSyncParamsSchema = z.infer<typeof GetCaseSyncParamsSchema>;

export const CaseTitleFieldSchema = z
	.string()
	.min(1, "Field is required")
	.max(50, "Title must be less than 50 characters");

export const CaseTitleFormSchema = z.object({
	title: CaseTitleFieldSchema,
});

export type CaseTitleFormSchema = z.infer<typeof CaseTitleFormSchema>;

export const YearOfBirthSchema = z
	.number()
	.int("Year of birth must be an integer")
	.min(1750, "Year of birth must be at least 1750");

export const BaseCasePatientDetailFormSchema = z.object({
	surname: z.string(),
	name: z.string(),
	year_of_birth: YearOfBirthSchema,
	dob: z.string(),
	sex: z.enum(["Male", "Female", "Other"]),
	address: z.string(),
	ic_number: z.string().min(1, "Field is required"),
	email_address: z.string().email().or(z.literal("")),
	phone_number: z.string(),
});

export const CasePatientDetailFormSchema =
	BaseCasePatientDetailFormSchema.refine(
		(data) => {
			if (!data.dob) return true;
			const dob = new Date(data.dob);
			const year = dob.getFullYear();
			return year === data.year_of_birth;
		},
		{
			message: "Year of birth and date of birth do not match",
			path: ["dob"],
		},
	);

export type CasePatientDetailFormSchema = z.infer<
	typeof CasePatientDetailFormSchema
>;

export const UpdateCasePayloadSchema = z
	.object({
		id: DataIdSchema,
	})
	.merge(CaseTitleFormSchema)
	.merge(
		z.object({
			discharged_date: z.string(),
			discharged_time: z.string(),
			case_allergy_attributes: z.object({
				text: z.string(),
			}),
			case_diagnose_attributes: z.object({
				text: z.string(),
			}),
			case_medication_attributes: z.object({
				text: z.string(),
			}),
			case_plan_attributes: z.object({
				text: z.string(),
			}),
			case_summary_attributes: z.object({
				text: z.string(),
			}),
		}),
	)
	.partial({
		title: true,
		discharged_date: true,
		discharged_time: true,
		case_allergy_attributes: true,
		case_diagnose_attributes: true,
		case_medication_attributes: true,
		case_plan_attributes: true,
		case_summary_attributes: true,
	});

export type UpdateCasePayloadSchema = z.infer<typeof UpdateCasePayloadSchema>;

export const CreatePatientPayloadSchema = z
	.object({
		caseId: DataIdSchema.optional(),
	})
	.merge(BaseCasePatientDetailFormSchema)
	.partial({
		dob: true,
		sex: true,
		name: true,
		address: true,
		surname: true,
		email_address: true,
		phone_number: true,
	});

export type CreatePatientPayloadSchema = z.infer<
	typeof CreatePatientPayloadSchema
>;

export const UpdatePatientPayloadSchema = z
	.object({
		id: DataIdSchema,
		caseId: DataIdSchema.optional(),
	})
	.merge(BaseCasePatientDetailFormSchema)
	.partial({
		dob: true,
		sex: true,
		name: true,
		address: true,
		surname: true,
		ic_number: true,
		year_of_birth: true,
		email_address: true,
		phone_number: true,
	});

export type UpdatePatientPayloadSchema = z.infer<
	typeof UpdatePatientPayloadSchema
>;

export const PostAddLocationPayloadSchema = z.object({
	caseId: DataIdSchema,
	name: z.string().min(1, "Field is required"),
});

export type PostAddLocationPayloadSchema = z.infer<
	typeof PostAddLocationPayloadSchema
>;

export const PatientVisitFormSchema = z.object({
	caseId: DataIdSchema,
	date: z.any().transform((value) => {
		if (dayjs.isDayjs(value)) {
			const date = value.format("YYYY-MM-DD");
			return date;
		}
		return value;
	}),
	time: z.any().transform((value) => {
		if (dayjs.isDayjs(value)) {
			const time = value.format("hh:mm a");
			return time;
		}
		return value;
	}),
	location: z.string().min(1, "Field is required"),
	department: z.string().min(1, "Field is required"),
	seen_by: z.string().min(1, "Field is required"),
});

export type PatientVisitFormSchema = z.infer<typeof PatientVisitFormSchema>;

export const DischargeNoteFormSchema = z.object({
	date: z.any().transform((val) => {
		if (dayjs.isDayjs(val)) {
			return val.format("DD/MM/YYYY");
		}
		return val;
	}),
	time: z.any().transform((val) => {
		if (dayjs.isDayjs(val)) {
			return val.format("hh:mm a");
		}
		return val;
	}),
	diagnosis: z.string().min(1, "Field is required"),
	medication: z.string().min(1, "Field is required"),
	allergies: z.string().min(1, "Field is required"),
	summary: z.string().min(1, "Field is required"),
	plan: z.string().min(1, "Field is required"),
});

export type DischargeNoteFormSchema = z.infer<typeof DischargeNoteFormSchema>;

export const CreateCasePayloadSchema = z.object({
	year_of_birth: z.number().int(),
	sex: z.enum(["Male", "Female", "Other"]),
	title: z.string().min(1, "Field is required"),
	summary: z.string().optional(),
});

export type CreateCasePayloadSchema = z.infer<typeof CreateCasePayloadSchema>;

export const CreateCaseInfoPayloadSchema = z.object({
	caseId: DataIdSchema,
	info_type: z.enum(["summaries", "allergies", "medication"]),
	description: z.string().min(1, "Field is required"),
});

export type CreateCaseInfoPayloadSchema = z.infer<
	typeof CreateCaseInfoPayloadSchema
>;

export const AddRemoveMemberCasePayloadSchema = z.object({
	caseId: DataIdSchema,
	user_ids: z.array(DataIdSchema),
	user_aidbox_ids: z.array(z.string()).optional(),
});

export type AddRemoveMemberCasePayloadSchema = z.infer<
	typeof AddRemoveMemberCasePayloadSchema
>;

export const InviteMemberAndTeamsFormSchema = z.object({
	caseId: DataIdSchema,
	member_ids: z.array(DataIdSchema),
	team_ids: z.array(DataIdSchema),
});

export type InviteMemberAndTeamsFormSchema = z.infer<
	typeof InviteMemberAndTeamsFormSchema
>;

export const CreateCaseExisitingPatientPayloadSchema = z.object({
	patient_ic_number: z.string().min(1, "Field is required"),
});
export type CreateCaseExisitingPatientPayloadSchema = z.infer<
	typeof CreateCaseExisitingPatientPayloadSchema
>;

export const ExportCasePayloadSchema = z.object({
	caseId: DataIdSchema,
	emails: z.string().trim().email(),
	note: z.string().max(200).optional(),
});
export type ExportCasePayloadSchema = z.infer<typeof ExportCasePayloadSchema>;

export const CreateRequestAccessCasePayloadSchema = z.object({
	caseId: DataIdSchema,
	message: z.string().trim().min(1, "Message is required"),
});
export type CreateRequestAccessCasePayloadSchema = z.infer<
	typeof CreateRequestAccessCasePayloadSchema
>;

export const ExistPatientRequestCaseSearchSchema =
	CreateCaseSearchParamSchema.pick({
		pid: true,
		pdob: true,
		psex: true,
	}).merge(
		z.object({
			case_name: z.string().trim().optional(),
		}),
	);
export type ExistPatientRequestCaseSearchSchema = z.infer<
	typeof ExistPatientRequestCaseSearchSchema
>;

export const MedicalNoteFormSchema = z.object({
	id: DataIdSchema,
	text: z.string().min(1, "Field is required"),
	status: z.enum(["draft", "final"]).optional(),
	wound_analysis_id: z.coerce.number().optional(),
});
export type MedicalNoteFormSchema = z.infer<typeof MedicalNoteFormSchema>;

export const UpdateMedicalNoteFormSchema = MedicalNoteFormSchema.merge(
	z.object({
		caseId: DataIdSchema,
	}),
);
export type UpdateMedicalNoteFormSchema = z.infer<
	typeof UpdateMedicalNoteFormSchema
>;

export const CaseReferralFormFieldSchema = z.object({
	channelName: z.string(),
	caseId: z.coerce.number().default(0),
	tags: z.enum([
		MESSAGE_TAG.REFERAL,
		MESSAGE_TAG.HANDOVER,
		MESSAGE_TAG.SPECIALISTS_ADVICE,
	]),
	text: z
		.string()
		.max(300, "Publish Description must not exceed 300 characters"),
	member: z.custom<ContactMember>(),
	team: z.custom<CaseTeam>(),
	_case: z.custom<Case>(),
});
export type CaseReferralFormFieldSchema = z.infer<
	typeof CaseReferralFormFieldSchema
>;

export const NewCaseLetterFormFieldSchema = z.object({
	caseId: DataIdSchema,
	case_letter: z.object({
		letter_type: z.nativeEnum(LETTER_TYPE),
		date: z.custom<Dayjs>(),
		time: z.custom<Dayjs>(),
		diagnosis: z.string().optional(),
		medication: z.string().optional(),
		allergies: z.string().optional(),
		summary: z.string().optional(),
		plan: z.string().optional(),
	}),
});
export type NewCaseLetterFormFieldSchema = z.infer<
	typeof NewCaseLetterFormFieldSchema
>;

export const UpdateCaseLetterFormFieldSchema =
	NewCaseLetterFormFieldSchema.pick({
		case_letter: true,
	}).merge(
		z.object({
			id: DataIdSchema,
		}),
	);
export type UpdateCaseLetterFormFieldSchema = z.infer<
	typeof UpdateCaseLetterFormFieldSchema
>;

export const SendCaseLetterFormFieldSchema = z.object({
	id: DataIdSchema,
	emails: z.string().min(1, "Email is required"),
	note: z.string().min(1, "Note is required"),
});
export type SendCaseLetterFormFieldSchema = z.infer<
	typeof SendCaseLetterFormFieldSchema
>;

export const EmailListSchema = z
	.string()
	.transform((val) => val.split(/\s*,\s*/).filter(Boolean))
	.refine(
		(emails) =>
			emails.every((email) => z.string().email().safeParse(email).success),
		{
			message: "One or more emails are invalid",
		},
	)
	.transform((emails) => emails as string[]);
